import { Group } from "../models/interfaces/group";
import Hierarchy from "../models/interfaces/hierarchy";
import { ListOptions } from "../models/interfaces/list-options";
import { MskScore } from "../models/interfaces/msk-score";
import { Organization } from "../models/interfaces/organization";
import { User } from "../models/interfaces/user";
import { UserClaims } from "../models/interfaces/user-claims";
import UserRoles from "../models/user-roles";
import ArticleUtils from "./article-utils";
import { CorrectiveProgramUtils } from "./corrective-program-utils";
import GroupService from "./services/group-service";
import OrganizationService from "./services/organization-service";
import UserService from "./services/user-service";
import { WellnessProgram } from "../models/interfaces/wellness-program";
import UserMskFocusArticlesUtil from "./user-msk-focus-articles-util";
import UserCorrectiveProgramsService from "./services/corrective-programs-service/user-corrective-programs-service";
import { addDoc, collection, doc, getDocs, query, runTransaction, serverTimestamp, where } from "firebase/firestore";
import getFirebase from "./firebase";
import ErrorLogService from "./services/error-log-service";
import { createLogger } from "./logger";

const log = createLogger("UserUtil");
const { db } = getFirebase();

export default class UserUtil {
    // -----------------------------------------------------------------------------------------
    // #region Public Methods
    // -----------------------------------------------------------------------------------------

    public static async getOrganizationUsersOptions(orgId: string): Promise<ListOptions[]> {
        const users = await UserService.getAllByOrganizationId(orgId);

        if (!users || users.length === 0) {
            return [];
        }

        const options: ListOptions[] = [];

        users.forEach((u: User) => {
            const option = this.userToListOption(u);
            if (option !== null && option !== undefined) {
                options.push(option);
            }
        });

        return options;
    }

    /**
     * Get list options for all super admin users. Label is first and/or last
     * name or email. Value is id.
     * @returns {Promise<ListOptions[]>} A promise for super admin list options
     */
    public static async getSuperAdminDropdownOptions(): Promise<ListOptions[]> {
        const superAdmins = await this.getSuperAdmins();

        if (!superAdmins || superAdmins.length === 0) {
            return [];
        }

        const options: ListOptions[] = [];

        superAdmins.forEach((sa) => {
            const option = this.userToListOption(sa);
            if (option !== null && option !== undefined) {
                options.push(option);
            }
        })

        return options;
    }

    /**
     * Get all super admin users
     * @returns {Promise<User[]>} a promise for all super admin users
     */
    public static async getSuperAdmins(): Promise<User[]> {
        return UserService.getBy([{
            field: 'isSuperAdmin',
            operator: '==',
            value: true,
        }]);
    }

    /**
     * Determines if a given user is a developer based on the domain of their
     * email address.
     *
     * @param {User} u The user to check
     * @returns {boolean} True if the user is a developer, false if they are
     * not.
     */
    public static isDeveloperUser(u: User): boolean {
        if (u.email) {
            const atIndex = u.email.indexOf('@');
            const domain = u.email.substring(atIndex + 1);
            return domain === 'dstrat.com';
        }

        return false;
    }

    /**
     * User is a super admin
     */
    public static isSuperAdmin(userClaims: UserClaims | null | undefined): boolean {
        if (userClaims == null) {
            return false;
        }
        return userClaims.superAdmin === true;
    }

    /**
     * User is an account holder
     */
    public static isAccountHolder(user: User | UserClaims | null | undefined): boolean {
        return UserUtil.belongsToRole(user, UserRoles.ACCOUNT_HOLDER_ID);
    }

    /**
     * User is an account holder
     */
    public static isClient(user: User | UserClaims | null | undefined): boolean {
        return UserUtil.belongsToRole(user, UserRoles.CLIENT_ID);
    }

    /**
     * User is an admin
     */
    public static isAdmin(user: User | UserClaims | null | undefined): boolean {
        return UserUtil.belongsToRole(user, UserRoles.ADMIN_ID);
    }

    /**
     * User is a manager (and not an admin)
     */
    public static isManager(user: User | UserClaims | null | undefined): boolean {
        return !UserUtil.belongsToRole(user, UserRoles.ADMIN_ID)
            && UserUtil.belongsToRole(user, UserRoles.GROUP_MANAGER_ID);
    }

    /**
     * User is a manager or team member (and not an admin)
    */
    public static isManagerOrTeamMember(user: User | UserClaims | null | undefined): boolean {
        if (UserUtil.belongsToRole(user, UserRoles.ADMIN_ID)) {
            return false;
        }
        return UserUtil.belongsToRole(user, UserRoles.GROUP_MANAGER_ID)
            || UserUtil.belongsToRole(user, UserRoles.TEAM_MEMBER_ID);
    }

    /**
     * User is a team member (and not an admin nor manager)
     */
    public static isTeamMember(user: User | UserClaims | null | undefined): boolean {
        return !UserUtil.belongsToRole(user, UserRoles.ADMIN_ID)
            && !UserUtil.belongsToRole(user, UserRoles.GROUP_MANAGER_ID)
            && UserUtil.belongsToRole(user, UserRoles.TEAM_MEMBER_ID);

    }

    /**
     * Gets a count of all users in organizations administrated by an account holder.
     * 
     * @param {string | null | undefined} userId The account holder user.
     * @returns {promise} A promise for the number of users under the account holder
     */
    public static async getAccountHolderUserCount(userId: string | null | undefined, organizationId: string | null | undefined): Promise<number> {
        if (!userId || !organizationId) {
            return 0;
        }
        const holderOrgs = await OrganizationService.getBy([{
            field: 'accountHolderId',
            operator: '==',
            value: userId,
        }]);

        let currentUsers = 0;
        const promises: Promise<number>[] = [];

        holderOrgs.forEach(async (o) => {
            promises.push(this.getOrganizationUserCount(o.id));
        });

        await Promise.all(promises).then((r) => {
            r.forEach((c) => currentUsers += c);
        });

        return currentUsers;
    };

    public static async getOrganizationUserCount(organizationId: string | null | undefined): Promise<number> {
        if (!organizationId) {
            return 0;
        }
        const users = await UserService.getBy([{
            field: 'organizationId',
            operator: '==',
            value: organizationId,
        }]);

        return users.length;
    };

    /**
     * Gets a count of all organizations administrated by an account holder.
     * 
     * @param {User | null | undefined} user The account holder user.
     * @returns {promise} A promise for the number of users under the account holder
     */
    public static async getAccountHolderOrgCount(user: User | null | undefined): Promise<number> {
        if (!user) {
            return 0;
        }
        const holderOrgs = await OrganizationService.getBy([{
            field: 'accountHolderId',
            operator: '==',
            value: user?.id,
        }]);
        return holderOrgs.length;
    };

    /**
     * User is a manager (and not an admin)
     */
    public static getDefaultManageGroupId(user: User | null | undefined): string | null {
        if (user == null || !UserUtil.isManagerOrTeamMember(user)) {
            return null;
        }

        const hasManagedGroups = user?.managedGroupIds != null && user.managedGroupIds.length >= 1;
        if (!hasManagedGroups) {
            return null;
        }

        // user manages their group
        if (user.groupId != null && user.managedGroupIds!.indexOf(user.groupId) >= 0) {
            return user.groupId;
        }

        // first manage group
        return user.managedGroupIds![0];
    }

    /**
     * User has no role
     */
    public static hasNoRole(user: User | UserClaims | null | undefined): boolean {
        return user?.roles == null || user.roles.length === 0;
    }

    public static belongsToRole(
        user: User | UserClaims | null | undefined,
        roleId?: string
    ): boolean {
        if (user?.roles == null || user.roles.length === 0 || roleId == null) {
            return false;
        }
        return user.roles.indexOf(roleId) >= 0;
    }

    /**
     * Filters the list by groups that you manage
     */
    public static getManagedGroups(allGroups: Group[], user: User | null): Group[] {
        const isAdmin = UserUtil.belongsToRole(user, "admin");

        if (isAdmin) {
            return allGroups;
        }

        return allGroups.filter((group) => UserUtil.canManageGroup(user, group));
    }

    /**
     * Handled roles: ADMIN_ID and UserRoles.GROUP_MANAGER_ID
     */
    public static canManageGroup(
        user: User | null | undefined,
        group?: Hierarchy,
        allowEmptyGroupId: boolean = true
    ): boolean {
        if (user?.roles == null || user.roles.length === 0 || group?.id == null) {
            return false;
        }

        if (allowEmptyGroupId && group.id === "") {
            return true;
        }

        const isAdmin = this.belongsToRole(user, UserRoles.ADMIN_ID);
        if (isAdmin) {
            return true;
        }

        const isAccountHolder = this.isAccountHolder(user);
        if (isAccountHolder) {
            return true;
        }

        const isManager = this.isManagerOrTeamMember(user);
        if (!isManager) {
            return false;
        }

        if (user?.managedGroupIds == null || user.managedGroupIds.length === 0) {
            return false;
        }

        if (user.managedGroupIds.indexOf(group.id.toString()) >= 0) {
            return true;
        }
        if (group.parentIds != null && group.parentIds.length >= 0) {
            for (let i = 0; i < group.parentIds.length; i++) {
                if (user.managedGroupIds.indexOf(group.parentIds[i].toString()) >= 0) {
                    return true;
                }
            }
        }

        return false;
    }

    public static async getAlgoliaAccountHolderOrganizationFilter(
        user: User | null | undefined,
    ): Promise<string> {
        const orgIds = await this.getAccountHolderOrganizationIds(user);

        if (orgIds.length === 0) {
            return '';
        }

        return orgIds.map((id) => `id:${id}`).join(' OR ');
    };

    /**
     * Returns an Algolia filter matching only the account holder's
     * sub-organizations (the head organization itself is excluded). Used by
     * the organization management list, which is a sub-organization-only view
     * for account holders. Returns a NO_MATCH filter when there are no
     * sub-organizations.
     */
    public static async getAlgoliaSubOrganizationFilter(
        user: User | null | undefined,
    ): Promise<string> {
        if (!user?.organizationId) {
            return `id:"NO_MATCH"`;
        }

        const holderOrg = await OrganizationService.get(user.organizationId);

        const subOrgIds = holderOrg?.subOrganizationIds ?? [];

        if (subOrgIds.length === 0) {
            return `id:"NO_MATCH"`;
        }

        return subOrgIds.map((id) => `id:${id}`).join(' OR ');
    };

    /**
     * Returns the organization ids the account holder can access: their own
     * organization plus all of its sub-organizations (from the head's derived
     * subOrganizationIds cache). Sub-organizations never carry an
     * accountHolderId, so we enumerate through the head instead of querying by
     * accountHolderId.
     */
    private static async getAccountHolderOrganizationIds(user: User | null | undefined): Promise<string[]> {
        if (!user?.organizationId) {
            return [];
        }

        const holderOrg = await OrganizationService.get(user.organizationId);

        const orgIds: string[] = [user.organizationId];

        if (holderOrg?.subOrganizationIds) {
            orgIds.push(...holderOrg.subOrganizationIds);
        }

        return orgIds;
    };

    /**
     * Returns the organization ids a user can access: their own organization
     * plus, for account holders, all of its sub-organizations, or for admins,
     * the sub-organizations explicitly granted to them via
     * accessibleSubOrganizationIds. Super admins access every organization
     * (represented by an empty filter string at the Algolia layer).
     */
    public static async getAccessibleOrganizationIds(user: User | null | undefined): Promise<string[]> {
        if (user == null || !user.organizationId) {
            return [];
        }

        if (this.isAccountHolder(user)) {
            return this.getAccountHolderOrganizationIds(user);
        }

        if (this.belongsToRole(user, UserRoles.ADMIN_ID)) {
            return [user.organizationId, ...(user.accessibleSubOrganizationIds ?? [])];
        }

        return [user.organizationId];
    };

    /**
     * Returns an Algolia filter matching every organization a user can switch
     * into: super admins get everything (empty filter), account holders get
     * their head + sub-organizations, admins get their head + granted
     * sub-organizations. Returns a NO_MATCH filter when there is nothing to
     * switch into.
     */
    public static async getAlgoliaAccessibleOrganizationFilter(
        user: User | null | undefined,
    ): Promise<string> {
        if (user == null) {
            return `id:"NO_MATCH"`;
        }

        if (user.isSuperAdmin) {
            return '';
        }

        const orgIds = await this.getAccessibleOrganizationIds(user);

        if (orgIds.length === 0) {
            return `id:"NO_MATCH"`;
        }

        return orgIds.map((id) => `id:${id}`).join(' OR ');
    };

    /**
     * Determines whether a user can access a given organization: their own
     * organization or a sub-organization explicitly granted to an admin.
     * Super admins access everything. Account holders resolve their
     * sub-organizations through the head organization (see
     * getAccessibleOrganizationIds) and are not resolved here.
     */
    public static canAccessOrganization(
        user: User | null | undefined,
        organizationId?: string | null,
    ): boolean {
        if (user == null || !organizationId) {
            return false;
        }

        if (user.isSuperAdmin) {
            return true;
        }

        if (organizationId === user.organizationId) {
            return true;
        }

        return this.isAdmin(user) && (user.accessibleSubOrganizationIds?.indexOf(organizationId) ?? -1) >= 0;
    };

    /**
     * Determines whether the user should be offered the organization switcher:
     * super admins, account holders, and admins granted access to at least one
     * sub-organization.
     */
    public static canSwitchOrganizations(
        user: User | null | undefined,
        claims: UserClaims | null | undefined,
    ): boolean {
        if (this.isSuperAdmin(claims)) {
            return true;
        }

        if (this.isAccountHolder(user)) {
            return true;
        }

        return this.isAdmin(user) && (user?.accessibleSubOrganizationIds?.length ?? 0) > 0;
    };

    /**
     * Returns the sub-organizations the creating user is allowed to grant to a
     * new admin in the given head organization. Account holders and super
     * admins may grant every sub-organization of the head; admins may only
     * grant the sub-organizations they themselves have been granted.
     *
     * @param {User | null | undefined} user The user creating the admin.
     * @param {Organization} headOrg The head (non sub) organization the admin
     * is being created in.
     * @returns {Promise<Organization[]>} A promise for the grantable sub-orgs.
     */
    public static async getAdminSubOrganizationAccessOptions(
        user: User | null | undefined,
        headOrg: Organization | null | undefined,
    ): Promise<Organization[]> {
        if (headOrg == null || (headOrg.subOrganizationIds?.length ?? 0) === 0) {
            return [];
        }

        const subOrgs = await OrganizationService.getBy([{
            field: 'parentOrganizationId',
            operator: '==',
            value: headOrg.id,
        }]);

        if (user?.isSuperAdmin || this.isAccountHolder(user)) {
            return subOrgs;
        }

        if (this.belongsToRole(user, UserRoles.ADMIN_ID)) {
            const allowedIds = user?.accessibleSubOrganizationIds ?? [];
            return subOrgs.filter((s) => s.id != null && allowedIds.indexOf(s.id) >= 0);
        }

        return [];
    };

    public static async getAlgoliaUserOrganizationAndGroupFilter(
        user: User | null | undefined,
        organizationId?: string
    ): Promise<string> {
        if (user == null) {
            return `organizationId:"NO_MATCH"`;
        }
        if (organizationId == null) {
            if (user.isSuperAdmin) {
                return `NOT roles:client`;
            }
            if (this.isAccountHolder(user) || UserUtil.belongsToRole(user, UserRoles.ADMIN_ID)) {
                // Account holders get their head + all sub-organizations;
                // admins get their head + the sub-organizations they have
                // been granted access to.
                const orgIds = await this.getAccessibleOrganizationIds(user);
                if (orgIds.length === 0) {
                    return `organizationId:"NO_MATCH"`;
                }
                // build a string to search on all of the accessible org ids
                let filterString = orgIds.map((id) => `organizationId:${id}`).join(' OR ');
                return `(${filterString}) AND NOT roles:client`;
            }
            return `organizationId:"NO_MATCH"`;
        }

        if (UserUtil.belongsToRole(user, UserRoles.ADMIN_ID) || this.isAccountHolder(user)) {
            return `(organizationId:${organizationId}) AND NOT roles:client`;
        }

        if (UserUtil.belongsToRole(user, UserRoles.GROUP_MANAGER_ID)) {
            const managedGroupIds = await UserUtil.getHierarchicalGroupIds(user?.managedGroupIds);
            if (managedGroupIds.length === 0) {
                return `(organizationId:"${organizationId}" AND groupId:"NO_MATCH") AND NOT roles:client`;
            }

            // Generates: `groupId:"1" OR groupId:"2" OR groupId:"3"`
            const groupFilter = `groupId:"` + managedGroupIds.join(`" OR groupId:"`) + `"`;
            return `organizationId:"${organizationId}" AND (${groupFilter}) AND NOT roles:client`;
        }

        return `organizationId:"NO_MATCH"`;
    }

    public static async getAlgoliaUserOrganizationAndGroupFilterByClient(
        user: User | null | undefined,
        organizationId?: string
    ): Promise<string> {
        if (user == null) {
            return `organizationId:"NO_MATCH"`;
        }
        if (organizationId == null) {
            if (user.isSuperAdmin) {
                return `roles:client`;
            }
            if (this.isAccountHolder(user) || UserUtil.belongsToRole(user, UserRoles.ADMIN_ID)) {
                // Account holders get their head + all sub-organizations;
                // admins get their head + the sub-organizations they have
                // been granted access to.
                const orgIds = await this.getAccessibleOrganizationIds(user);
                if (orgIds.length === 0) {
                    return `organizationId:"NO_MATCH"`;
                }
                // build a string to search on all of the accessible org ids
                let filterString = orgIds.map((id) => `organizationId:${id}`).join(' OR ');
                return `(${filterString}) AND roles:client`;
            }
            return `organizationId:"NO_MATCH"`;
        }

        if (UserUtil.belongsToRole(user, UserRoles.ADMIN_ID) || this.isAccountHolder(user)) {
            return `(organizationId:${organizationId}) AND roles:client`;
        }

        if (UserUtil.belongsToRole(user, UserRoles.GROUP_MANAGER_ID)) {
            const managedGroupIds = await UserUtil.getHierarchicalGroupIds(user?.managedGroupIds);
            if (managedGroupIds.length === 0) {
                return `(organizationId:"${organizationId}" AND groupId:"NO_MATCH") AND roles:client`;
            }

            // Generates: `groupId:"1" OR groupId:"2" OR groupId:"3"`
            const groupFilter = `groupId:"` + managedGroupIds.join(`" OR groupId:"`) + `"`;
            return `organizationId:"${organizationId}" AND (${groupFilter}) AND roles:client`;
        }

        return `organizationId:"NO_MATCH"`;
    }

    /**
     * Returns a list of all group IDs that are managed based on the parent group IDs, including the specified parent group IDs
     * @param parentGroupIds Parent level group IDs
     * @returns List of group IDs
     */
    public static async getHierarchicalGroupIds(parentGroupIds?: string[]): Promise<string[]> {
        const results: string[] = [];

        if (
            parentGroupIds == null ||
            !Array.isArray(parentGroupIds) ||
            parentGroupIds.length === 0
        ) {
            return results;
        }

        for (let i = 0; i < parentGroupIds.length; i++) {
            // skip if already in the collection
            const groupId = parentGroupIds[i];
            if (results.find((r) => r === groupId) != null) {
                continue;
            }

            // get child groups
            const parentGroups = await GroupService.getBy([
                {
                    field: "parentIds",
                    operator: "array-contains",
                    value: groupId,
                },
            ]);
            parentGroups.forEach((parentGroup) => {
                if (results.find((r) => r === parentGroup.id) == null) {
                    results.push(parentGroup.id);
                }
            });

            // get specific group
            if (results.find((r) => r === groupId) == null) {
                results.push(groupId);
            }
        }

        return results;
    }

    // #endregion Public Methods

    private static userToListOption = (user: User) => {
        if (user.id) {
            let label = '';

            // if the super admin has no first or last name, use email
            if (!user.firstName && !user.lastName && user.email) {
                label = user.email;
            }

            // if the super admin has a first or last name, use one or both
            if (user.firstName || user.lastName) {
                label = `${user.firstName ? user.firstName.concat(' ') : ''}${user.lastName ?? ''}`
            }

            if (label) {
                const option: ListOptions = {
                    value: user.id,
                    label,
                }
                return option;
            }

            return null;
        }
    };

    public static async getCorrectiveProgram(
        mskScore: MskScore,
        userId: string,
        dayToShow?: number,
    ) {
        // determine if exercises should be loaded
        const loadCorrectiveExercises =
            mskScore?.movementScore?.percentage != null;

        if (!loadCorrectiveExercises) {
            log.debug("returning early, no correctives to load");
            return null;
        }

        // load exercises
        const todaysExercise = await CorrectiveProgramUtils.getTodaysCorrectiveProgramRoutine(
            userId,
            dayToShow,
        ).catch((error: any) => {
            log.error("Failed to load today's corrective program routine", {
                exception: error,
                userId,
            });
            return null;
        });

        if (todaysExercise?.errors != null && todaysExercise.errors?.length >= 1) {
            log.error("There are errors in todaysExercise", {
                errors: todaysExercise.errors,
                userId,
            });
            return null;
        }

        // load exercises
        const todaysExtraExercise = await CorrectiveProgramUtils.getTodaysExtraCorrectiveProgramRoutine(
            userId,
            dayToShow,
        ).catch((error: any) => {
            log.error("Failed to load today's extra corrective program routine", {
                exception: error,
                userId,
            });
            return null;
        });


        if (todaysExtraExercise?.errors != null && todaysExtraExercise.errors?.length >= 1) {
            log.error("There are errors in todaysExtraExercise", {
                errors: todaysExtraExercise.errors,
                userId,
            });
            return null;
        }

        return { correctiveProgram: todaysExercise, extraCorrectiveProgram: todaysExtraExercise };
    }

    public static async getUserArticle(
        mskScore: MskScore,
        userId: string,
        dayToShow?: number,
    ) {
        // determine if articles should be loaded
        const loadArticles =
            // requires a focus area
            mskScore?.focusAreas != null &&
            mskScore?.focusAreas.length >= 1;
        if (!loadArticles) {
            log.debug("returning early, no articles to load");
            return null;
        }

        // load articles
        const tagPriority = ArticleUtils.getTagPriority(mskScore);
        const todaysArticles = await ArticleUtils.getTodaysArticles(
            userId,
            tagPriority,
            dayToShow,
        ).catch((error: any) => {
            log.error("Failed to load today's articles", {
                exception: error,
                userId,
            });
            return null;
        });
        if (todaysArticles == null || todaysArticles.length === 0) {
            return null;

        }

        return todaysArticles;
    }

    public static async handleRetestClick(user: User, wellnessProgram: WellnessProgram, mskScore: MskScore, administeredRetake: boolean = false, adminUser?: User) {
        if (wellnessProgram && user && user.id && mskScore) {
            try {
                await runTransaction(db, async (transaction) => {
                    // Fetch current UserMskFocusArticle
                    const userMskFocusArticle = await UserMskFocusArticlesUtil.getCurrentByUserId(user.id!);
                    if (userMskFocusArticle && userMskFocusArticle.id) {
                        const userMskFocusArticleRef = doc(db, "userMskFocusArticles", userMskFocusArticle.id);
                        transaction.update(userMskFocusArticleRef, { isCurrent: false });
                    }

                    // Fetch and update all current UserCorrectiveExercises
                    const userCorrectiveExercises = await UserCorrectiveProgramsService.getAllCurrentByUserId(user.id!);
                    if (userCorrectiveExercises && userCorrectiveExercises.length > 0) {
                        for (const exercise of userCorrectiveExercises) {
                            if (!exercise.id) {
                                throw new Error(`Exercise ID is missing for one of the exercises.`);
                            }
                            const exerciseRef = doc(db, "userCorrectiveExercises", exercise.id);
                            transaction.update(exerciseRef, { isCurrent: false });
                        }
                    }

                    // Update wellness program
                    const wellnessProgramRef = doc(db, "wellnessPrograms", wellnessProgram.id!);
                    transaction.update(wellnessProgramRef, { isCurrent: false });

                    // Update user fields
                    const userRef = doc(db, "users", user.id!);
                    transaction.update(userRef, {
                        wellnessProgramId: "",
                        administeredRetake: administeredRetake
                    });

                    // Delete today object to ensure user can regenerate today.
                    // Fetch and delete all today's documents for the user
                    const todayCollectionRef = collection(db, "today");
                    const todayQuery = query(todayCollectionRef, where("userId", "==", user.id!));
                    const todayQuerySnapshot = await getDocs(todayQuery);

                    todayQuerySnapshot.forEach((docSnapshot) => {
                        const docRef = doc(db, "today", docSnapshot.id);
                        transaction.delete(docRef);
                    });

                    // Add log with auto-generated ID
                    const logData = {
                        userId: user.id,
                        message: "User's wellness program expired. Caught in user-util.ts handleRetestClick()",
                        data: [user, wellnessProgram],
                        created: serverTimestamp()
                    };
                    await addDoc(collection(db, "logs"), logData);

                });
                log.info("Retest administered", {
                    subjectUserId: user.id,
                    wellnessProgramId: wellnessProgram?.id,
                    administeredRetake,
                });
            } catch (error: any) {
                // Extract relevant error information
                const errorData = {
                    message: error.message,
                    name: error.name,
                    stack: error.stack,
                };
                log.error("Couldn't complete wellness program retest transaction logic", {
                    exception: error,
                    subjectUserId: user.id,
                });
                ErrorLogService.add({
                    userId: user.id,
                    message: "Couldn't complete wellness program retest transaction logic",
                    data: [errorData]
                })
                throw new Error("Couldn't complete transaction, passing to parent");
            }
        };
    }
}
